import { motion } from "framer-motion";
import { Loader2, RefreshCw } from "lucide-react";
import type { ComponentType } from "react";

import { WidgetAccessoModuli } from "@/components/dashboard/WidgetAccessoModuli";
import { WidgetAssistentePresidenza } from "@/components/dashboard/WidgetAssistentePresidenza";
import { WidgetCard } from "@/components/dashboard/WidgetCard";
import { WidgetClientiCommerciali } from "@/components/dashboard/WidgetClientiCommerciali";
import { WidgetCosaFareOggi } from "@/components/dashboard/WidgetCosaFareOggi";
import { WidgetDecisioniAnomalie } from "@/components/dashboard/WidgetDecisioniAnomalie";
import { WidgetDevBand } from "@/components/dashboard/WidgetDevBand";
import { WidgetErrorBoundary } from "@/components/dashboard/WidgetErrorBoundary";
import { WidgetKpiDirezionali } from "@/components/dashboard/WidgetKpiDirezionali";
import { ModuloCard, type ModuloCardConfig } from "@/components/dashboard/ModuloCard";
import { WidgetOmcProgress } from "@/components/dashboard/WidgetOmcProgress";
import { WidgetPipelineLogistica } from "@/components/dashboard/WidgetPipelineLogistica";
import { WidgetPlaceholderBody } from "@/components/dashboard/WidgetPlaceholderBody";
import { WidgetPorta } from "@/components/dashboard/WidgetPorta";
import { WidgetRecenti } from "@/components/dashboard/WidgetRecenti";
import { WidgetStatoDati } from "@/components/dashboard/WidgetStatoDati";
import { WidgetTeamModuli } from "@/components/dashboard/WidgetTeamModuli";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDashboardRefresh,
  useDashboardWidgets,
} from "@/hooks/useDashboard";
import { useMe } from "@/hooks/useMe";
import { cn } from "@/lib/utils";
import type { DashboardWidget } from "@/types/api";

/**
 * Mapping codice widget di tipo `contenuto` → componente body. I widget `modulo`
 * e `porta` non passano da qui: sono renderizzati dal loro componente dedicato
 * (vedi WidgetRenderer).
 */
const WIDGET_REGISTRY: Record<string, ComponentType<{ widget: DashboardWidget }>> = {
  stato_dati: WidgetStatoDati,
  pipeline_logistica: WidgetPipelineLogistica,
  kpi_direzionali: WidgetKpiDirezionali,
  cosa_fare_oggi: WidgetCosaFareOggi,
  recenti: WidgetRecenti,
  accesso_moduli: WidgetAccessoModuli,
  decisioni_anomalie: WidgetDecisioniAnomalie,
  clienti_commerciali: WidgetClientiCommerciali,
  omc_progress: WidgetOmcProgress,
  assistente_presidenza: WidgetAssistentePresidenza,
  team_moduli: WidgetTeamModuli,
  dev_band: WidgetDevBand,
};

/**
 * Widget che renderizzano la PROPRIA card (header/chrome inclusi): non vanno
 * incartati in `WidgetCard`, eviterebbe il doppio header (es. OMC collassabile).
 */
const SELF_CONTAINED = new Set(["omc_progress", "assistente_presidenza"]);

/**
 * Span di griglia di default per codice (1 = una colonna, 2 = due, 3 = piena).
 * Sovrascrivibile dal widget via `config.span` (SPEC-A §9.1). I sottoinsiemi
 * per-ruolo restano leggibili: i widget "larghi" stanno in alto.
 */
const SPAN_DEFAULT: Record<string, number> = {
  cosa_fare_oggi: 3,
  kpi_direzionali: 3,
  omc_progress: 3,
  porta_progetto: 3,
  modulo_m3: 2,
  modulo_m1: 1,
  modulo_m2: 1,
  pipeline_logistica: 1,
  stato_dati: 2,
  team_moduli: 3,
  recenti: 3,
  dev_band: 3,
};

function spanClass(span: number): string {
  if (span >= 3) return "md:col-span-2 xl:col-span-3";
  if (span === 2) return "md:col-span-2 xl:col-span-2";
  return "";
}

/** Span dentro una riga composita a 5 colonne (es. anomalie 3 + assistente 2). */
function spanClass5(span: number): string {
  if (span >= 3) return "lg:col-span-3";
  if (span === 2) return "lg:col-span-2";
  return "";
}

function widgetSpan(w: DashboardWidget): number {
  return (
    (typeof w.config?.span === "number" ? (w.config.span as number) : undefined) ??
    SPAN_DEFAULT[w.codice] ??
    1
  );
}

type RenderItem =
  | { kind: "single"; widget: DashboardWidget }
  | { kind: "group"; key: string; widgets: DashboardWidget[] };

/**
 * Raggruppa widget CONSECUTIVI con lo stesso `config.row_group` in una riga
 * composita a 5 colonne (layout fuori dalla griglia generica a 3 colonne, così
 * non altera gli span degli altri widget). Cfr. SPEC M4 §1 / decisione A1.
 */
function raggruppa(widgets: DashboardWidget[]): RenderItem[] {
  const items: RenderItem[] = [];
  for (const w of widgets) {
    const rg = typeof w.config?.row_group === "string" ? w.config.row_group : null;
    if (rg) {
      const ultimo = items[items.length - 1];
      if (ultimo && ultimo.kind === "group" && ultimo.key === rg) {
        ultimo.widgets.push(w);
        continue;
      }
      items.push({ kind: "group", key: rg, widgets: [w] });
    } else {
      items.push({ kind: "single", widget: w });
    }
  }
  return items;
}


export function DashboardPage() {
  const { data: me } = useMe();
  const widgetsQuery = useDashboardWidgets();
  const refresh = useDashboardRefresh();

  const oggi = new Date().toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const nome = me?.nome_completo?.split(" ")[0] ?? "";
  const saluteString = nome ? `Ciao ${nome}` : "Ciao";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="mx-auto max-w-7xl px-6 py-10 space-y-6"
    >
      <header className="sticky top-0 z-10 -mx-6 flex flex-wrap items-start justify-between gap-4 bg-background/80 px-6 py-2 backdrop-blur">
        <div className="space-y-1">
          <p className="text-xs capitalize text-muted-foreground">{oggi}</p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {saluteString}
          </h1>
          <p className="max-w-3xl pt-1 text-sm text-muted-foreground">
            Mister Pet · Operations Hub
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={widgetsQuery.isFetching}
        >
          {widgetsQuery.isFetching ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          Aggiorna
        </Button>
      </header>

      {widgetsQuery.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-40 w-full md:col-span-2 xl:col-span-3" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : !widgetsQuery.data || widgetsQuery.data.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-muted/20 p-10 text-center text-sm text-muted-foreground">
          Nessun widget configurato per il tuo profilo. Contatta Maitime.
        </div>
      ) : (
        <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
          {raggruppa(widgetsQuery.data).map((item) =>
            item.kind === "single" ? (
              <div
                key={item.widget.id}
                className={cn(spanClass(widgetSpan(item.widget)))}
              >
                <WidgetErrorBoundary titolo={item.widget.titolo}>
                  <WidgetRenderer widget={item.widget} />
                </WidgetErrorBoundary>
              </div>
            ) : (
              <div key={item.key} className="md:col-span-2 xl:col-span-3">
                <div className="grid items-start gap-4 lg:grid-cols-5">
                  {item.widgets.map((w) => (
                    <div key={w.id} className={cn(spanClass5(widgetSpan(w)))}>
                      <WidgetErrorBoundary titolo={w.titolo}>
                        <WidgetRenderer widget={w} />
                      </WidgetErrorBoundary>
                    </div>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </motion.div>
  );
}

function WidgetRenderer({ widget }: { widget: DashboardWidget }) {
  if (widget.tipo === "modulo") {
    return (
      <ModuloCard
        titolo={widget.titolo}
        descrizione={widget.descrizione}
        config={(widget.config ?? null) as ModuloCardConfig | null}
      />
    );
  }

  if (widget.tipo === "porta") {
    return <WidgetPorta widget={widget} />;
  }

  const Body = WIDGET_REGISTRY[widget.codice];

  // Widget che renderizzano la propria card (no doppio header).
  if (Body && SELF_CONTAINED.has(widget.codice)) {
    return <Body widget={widget} />;
  }

  return (
    <WidgetCard
      titolo={widget.titolo}
      descrizione={widget.descrizione}
      placeholder={widget.placeholder}
    >
      {Body ? <Body widget={widget} /> : <WidgetPlaceholderBody />}
    </WidgetCard>
  );
}
